/**
 * The anonymous collector session id.
 *
 * Mirrors `src/lib/quizSession.ts` deliberately — same storage discipline, same
 * peek-vs-create split — because the two solve the identical problem and a
 * second, subtly different implementation is how they drift.
 *
 * This id is ONLY ever a pre-authentication placeholder. Nothing of value binds
 * to it permanently: a profile it owns is adopted into the account on sign-in,
 * and a discount code is never issued against it at all. That rule exists
 * because a won scratch-win prize bound to a localStorage session once became
 * unclaimable when the browser cleared.
 */

const SESSION_STORAGE_KEY = 'iv_collector_session';

/** Stable per-browser id, created on first call. */
export function getCollectorSessionId(): string {
  try {
    const existing = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const created = `vault-${crypto.randomUUID()}`;
    window.localStorage.setItem(SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    // Private browsing or a blocked storage partition. A per-call id still
    // rate-limits correctly; it just will not survive a reload.
    return `vault-${crypto.randomUUID()}`;
  }
}

/**
 * Read the id WITHOUT creating one.
 *
 * The adoption hook runs on every sign-in across the whole app, so it must not
 * mint a session for someone who has never played — that would write storage on
 * every visitor and make "has played" meaningless as a signal.
 */
export function peekCollectorSessionId(): string | null {
  try {
    return window.localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Forget the anonymous session once its profile belongs to an account. */
export function clearCollectorSessionId(): void {
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
